import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import CustomizationDialog from '../components/CustomizationDialog';
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import productService from '../services/productService';
import { FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, RadioGroup, Radio, FormGroup, Avatar, FormHelperText, Checkbox } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import categoryService from '../services/categoryService';
import customizationRulesService from '../services/customizationRulesService';
import { Product } from '../models/Product';
import { Category, CategoryCustomizationRules } from '../models/Category';
import { useAdminScope } from '../contexts/AdminScopeContext';
import ScopeRequirementAlert from '../components/ScopeRequirementAlert';
import {
  isMenuCatalogManagedByPosCaisse,
  MENU_CATALOG_POSCAISSE_MESSAGE,
} from '../utils/menuCatalogOwnership';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SettingsIcon from '@mui/icons-material/Settings';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarningIcon from '@mui/icons-material/Warning';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';

// Interface pour les règles de personnalisation associées à une catégorie
interface CategoryWithRules {
  category: Category;
  rules: CategoryCustomizationRules | null;
  productsCount: number;
  hasCommonRules: boolean;
}

// Type pour l'onglet actif
type TabType = 'categories' | 'products' | 'simulator';

const getRemovableIngredientLabels = (product?: Product | null): string[] => {
  const displayIngredients = product?.displayIngredients
    ?.filter((ingredient) => ingredient.removable !== false && ingredient.isDefaultIncluded !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((ingredient) => ingredient.label)
    .filter(Boolean);
  return displayIngredients && displayIngredients.length > 0 ? displayIngredients : (product?.ingredients || []);
};

const matchesCategoryForProduct = (product: Product, category: Category): boolean => {
  const productCategory = product.category || '';
  const normalizedProductCategory = productCategory.toLowerCase();
  const normalizedCategoryId = category.id.toLowerCase();
  const normalizedCategoryName = category.name.toLowerCase();

  return (
    product.categoryId === category.id ||
    productCategory === category.id ||
    normalizedProductCategory === normalizedCategoryId ||
    productCategory === category.name ||
    normalizedProductCategory === normalizedCategoryName ||
    productCategory === category.id.toUpperCase()
  );
};

const categoryHasCommonRules = (categoryProducts: Product[]): boolean => {
  const productsWithRules = categoryProducts.filter(
    (product) => product.customizationSections && product.customizationSections.length > 0
  );

  if (productsWithRules.length === 0) {
    return false;
  }

  const referenceSections = productsWithRules[0].customizationSections || [];

  return productsWithRules.every((product) => {
    const productSections = product.customizationSections || [];

    if (productSections.length !== referenceSections.length) {
      return false;
    }

    return referenceSections.every((referenceSection, index) => {
      const section = productSections[index];
      return (
        section?.type === referenceSection.type &&
        section?.title === referenceSection.title
      );
    });
  });
};

const Customizations: React.FC = () => {
  const { activeScopeType, selectedStoreId, selectedStore } = useAdminScope();
  const isCatalogReadOnly = isMenuCatalogManagedByPosCaisse(selectedStore);
  // États généraux
  const [activeTab, setActiveTab] = useState<TabType>('categories');
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesWithRules, setCategoriesWithRules] = useState<CategoryWithRules[]>([]);
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // États pour le dialogue de personnalisation
  const [customizationDialogOpen, setCustomizationDialogOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>('');
  const [existingRules, setExistingRules] = useState<CategoryCustomizationRules | null>(null);
  const [productsInheritingRules, setProductsInheritingRules] = useState<Record<string, boolean>>({});
  const [selectedProductIngredients, setSelectedProductIngredients] = useState<string[]>([]);
  
  // États pour le simulateur
  const [simulatorCategoryId, setSimulatorCategoryId] = useState<string>('');
  const [simulatorProductId, setSimulatorProductId] = useState<string>('');
  const [simSelections, setSimSelections] = useState<Record<string, string[]>>({});
  const [simRemovedIngredients, setSimRemovedIngredients] = useState<string[]>([]);
  const [simResultDialogOpen, setSimResultDialogOpen] = useState(false);
  const [simResult, setSimResult] = useState<{
    productName: string;
    basePrice: number;
    total: number;
    includedOptions: Array<{ id: string; name: string }>;
    paidSupplements: Array<{ id: string; name: string; price: number }>;
    removedIngredients: string[];
    finalIngredients: string[];
    suggestedProducts: Array<{ id: string; name: string }>;
    productImageUrl?: string | null;
    description?: string;
    xlPrice?: number | null;
    hasXL?: boolean;
  } | null>(null);
  const [simMobilePreviewOpen, setSimMobilePreviewOpen] = useState(false);
  const [mobilePreviewQuantity, setMobilePreviewQuantity] = useState(1);
  const [mobilePreviewSize, setMobilePreviewSize] = useState<'normal' | 'XL'>('normal');
  
  // États pour le dialogue de migration
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
// États pour les filtres Par produit
const [filterCategory, setFilterCategory] = useState<string>('');
const [searchTerm, setSearchTerm] = useState<string>('');
  const [migratingCategoryId, setMigratingCategoryId] = useState<string | null>(null);
  const [migrationProgress, setMigrationProgress] = useState<'idle' | 'running' | 'success' | 'error'>('idle');

  // États pour le dialogue de personnalisation par produit
  const [productCustomizationDialogOpen, setProductCustomizationDialogOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedProductName, setSelectedProductName] = useState<string>('');
  const [existingProductRules, setExistingProductRules] = useState<CategoryCustomizationRules | null>(null);

  // États pour le dialogue de confirmation de réinitialisation
  const [resetConfirmDialogOpen, setResetConfirmDialogOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [resetInProgress, setResetInProgress] = useState(false);

  // Charger les produits avec customisations et les catégories au chargement de la page
  useEffect(() => {
    if (activeScopeType === 'store' && selectedStoreId) {
      void loadCategoriesAndCustomizableProducts();
      return;
    }

    setCategories([]);
    setCategoryMap({});
    setCategoriesWithRules([]);
    setProducts([]);
    setLoading(false);
  }, [activeScopeType, selectedStoreId]);

  // Changer d'onglet
  const handleTabChange = (event: React.SyntheticEvent, newValue: TabType) => {
    setActiveTab(newValue);
  };

  // Fonction pour charger les catégories et produits
  const loadCategoriesAndCustomizableProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const [categoriesData, data, rulesData] = await Promise.all([
        categoryService.getCategories({ storeId: selectedStoreId }),
        productService.getProducts({ storeId: selectedStoreId || undefined }),
        customizationRulesService.getAllRules(),
      ]);

      setCategories(categoriesData);
      
      const catMap: Record<string, string> = {};
      categoriesData.forEach((cat) => {
        catMap[cat.id] = cat.name;
      });
      setCategoryMap(catMap);
      
      setProducts(data);

      const rulesByCategory = new Map(
        rulesData.map((rule) => [rule.category, rule] as const)
      );

      const categoriesWithRulesData: CategoryWithRules[] = categoriesData.map((category) => {
        const categoryProducts = data.filter((product) =>
          matchesCategoryForProduct(product, category)
        );

        return {
          category,
          rules: rulesByCategory.get(category.id) || null,
          productsCount: categoryProducts.length,
          hasCommonRules: categoryHasCommonRules(categoryProducts),
        };
      });
      
      setCategoriesWithRules(categoriesWithRulesData);
      
      const inheritanceMap: Record<string, boolean> = {};
      data.forEach((product) => {
        inheritanceMap[product.id] = product.usesCategoryRules || false;
      });
      setProductsInheritingRules(inheritanceMap);
      
    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError('Erreur lors du chargement des données. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Obtenir le nom d'une catégorie à partir de son ID
  const getCategoryName = (categoryId: string): string => {
    return categoryMap[categoryId] || 'Catégorie inconnue';
  };

  // Récupérer le titre de la section selon son type
  const getSectionTypeLabel = (type: string): string => {
    switch (type) {
      case 'included_options':
        return 'Options incluses';
      case 'paid_supplements':
        return 'Suppléments payants';
      case 'removable_ingredients':
        return 'Ingrédients supprimables';
      case 'suggested_products':
        return 'Produits suggérés';
      default:
        return type;
    }
  };

  // Helpers simulateur
  const matchesProductCategory = (productCategory: string | undefined, targetCategoryId: string): boolean => {
    if (!productCategory || !targetCategoryId) return false;
    const targetName = categoryMap[targetCategoryId];
    return (
      productCategory === targetCategoryId ||
      productCategory.toLowerCase() === targetCategoryId.toLowerCase() ||
      productCategory === (targetName || '') ||
      productCategory === targetCategoryId.toUpperCase()
    );
  };

  const getProductsByCategoryFlexible = (categoryId: string): Product[] => {
    if (!categoryId) return [];
    return products.filter(p => matchesProductCategory(p.category, categoryId));
  };

  const getCategoryRulesForProduct = (product: Product): CategoryCustomizationRules | null => {
    return categoriesWithRules.find(cwr =>
      product.categoryId === cwr.category.id ||
      product.categoryKey === cwr.category.id ||
      matchesProductCategory(product.category, cwr.category.id)
    )?.rules || null;
  };

  const getEffectiveCustomizationSections = (product: Product): any[] => {
    if (product.customizationSections && product.customizationSections.length > 0) {
      return product.customizationSections;
    }
    if (productsInheritingRules[product.id]) {
      return getCategoryRulesForProduct(product)?.sections || [];
    }
    return [];
  };

  const handleResetSimulatorSelections = () => {
    setSimSelections({});
    setSimRemovedIngredients([]);
  };

  // Ouvrir le dialogue pour éditer les règles d'une catégorie
  const handleEditCategoryRules = async (categoryId: string, ingredients: string[] = []) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setSelectedProductIngredients(ingredients);
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return;

    // Charger les règles au niveau de la catégorie ou initialiser à vide
    let rules = await customizationRulesService.getRulesForCategory(categoryId);
    if (!rules) {
      rules = { category: categoryId, sections: [] };
    }

    setSelectedCategoryId(categoryId);
    setSelectedCategoryName(category.name);
    setExistingRules(rules);
    setCustomizationDialogOpen(true);
  };
  
  // Fermer le dialogue
  const handleCloseCustomizationDialog = () => {
    setCustomizationDialogOpen(false);
    setSelectedCategoryId(null);
    setSelectedCategoryName('');
    setExistingRules(null);
  };
  
  // Sauvegarder les règles de personnalisation
  const handleSaveCustomizationRules = async (rules: CategoryCustomizationRules): Promise<void> => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      throw new Error(MENU_CATALOG_POSCAISSE_MESSAGE);
    }
    try {
      // Sauvegarder ou supprimer les règles au niveau de la catégorie
      if (rules.sections.length === 0) {
        await customizationRulesService.deleteRulesForCategory(rules.category);
        // Appliquer la suppression uniquement aux produits héritant des règles
        await customizationRulesService.applyRulesToAllCategoryProducts(rules.category);
      } else {
        await customizationRulesService.saveRulesForCategory(rules);
        // Appliquer les règles à tous les produits héritant des règles de catégorie
        await customizationRulesService.applyRulesToAllCategoryProducts(rules.category);
      }
      
      // Recharger les données
      await loadCategoriesAndCustomizableProducts();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde des règles:', err);
      setError('Erreur lors de la sauvegarde des règles. Veuillez réessayer.');
      throw err;
    }
  };
  
  // Afficher les sections de customisation d'un produit
  const renderCustomizationSections = (product: Product) => {
    const sections = getEffectiveCustomizationSections(product);
    if (!sections || sections.length === 0) {
      return <Typography variant="body2">Aucune section de customisation</Typography>;
    }

    return sections.map((section: any, index: number) => (
      <Box key={section.sectionId || index} mb={2}>
        <Typography variant="subtitle2" fontWeight="bold">
          {section.title || 'Section sans titre'}
        </Typography>
        <Box display="flex" gap={1} mt={0.5}>
          <Chip 
            label={getSectionTypeLabel(section.type)} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
          {section.minSelection && (
            <Chip 
              label={`Min: ${section.minSelection}`} 
              size="small" 
              color="default" 
              variant="outlined"
            />
          )}
          {section.maxSelection && (
            <Chip 
              label={`Max: ${section.maxSelection}`} 
              size="small" 
              color="default" 
              variant="outlined"
            />
          )}
          {(section.type === 'included_options' || section.type === 'paid_supplements') && section.addonCategory && (
            <Chip 
              label={`Cat: ${section.addonCategory}`} 
              size="small" 
              color="success" 
              variant="outlined"
            />
          )}
          {section.type === 'suggested_products' && section.productCategory && (
            <Chip 
              label={`Cat: ${section.productCategory}`} 
              size="small" 
              color="success" 
              variant="outlined"
            />
          )}
          {section.type === 'removable_ingredients' && (
            <Chip 
              label={`UI: ${section.selectionUI || 'checkbox'}`} 
              size="small" 
              color="default" 
              variant="outlined"
            />
          )}
        </Box>
      </Box>
    ));
  };

  // Gérer la migration des règles communes vers une catégorie
  const handleOpenMigrationDialog = (categoryId: string) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setMigratingCategoryId(categoryId);
    setMigrationProgress('idle');
    setMigrationDialogOpen(true);
  };
  
  // Démarrer le processus de migration
  const handleStartMigration = async () => {
    if (!migratingCategoryId) return;
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    
    try {
      setMigrationProgress('running');
      
      const success = await customizationRulesService.migrateToInheritance(migratingCategoryId);
      
      if (success) {
        setMigrationProgress('success');
        // Recharger les données après une migration réussie
        await loadCategoriesAndCustomizableProducts();
      } else {
        setMigrationProgress('error');
      }
    } catch (error) {
      console.error('Erreur lors de la migration:', error);
      setMigrationProgress('error');
    }
  };
  
  // Configurer l'héritage des règles pour un produit
  const handleToggleProductInheritance = async (productId: string, usesCategoryRules: boolean) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    try {
      await customizationRulesService.setProductUseCategoryRules(productId, usesCategoryRules);
      
      // Mettre à jour l'état local
      setProductsInheritingRules(prev => ({
        ...prev,
        [productId]: usesCategoryRules
      }));
      
      // Recharger les données pour refléter les changements
      await loadCategoriesAndCustomizableProducts();
    } catch (error) {
      console.error(`Erreur lors de la configuration de l'héritage pour le produit ${productId}:`, error);
      setError(`Erreur lors de la mise à jour de l'héritage des règles. Veuillez réessayer.`);
    }
  };

  // Réinitialiser un produit pour utiliser uniquement les règles de catégorie
  const handleResetProductRules = async (productId: string) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    try {
      await customizationRulesService.setProductUseCategoryRules(productId, true);
      await loadCategoriesAndCustomizableProducts();
    } catch (error) {
      console.error(`Erreur lors de la réinitialisation des règles pour le produit ${productId}:`, error);
      setError('Erreur lors de la réinitialisation des règles. Veuillez réessayer.');
    }
  };

  // Ouvrir le dialogue de confirmation pour la réinitialisation
  const handleOpenResetConfirmDialog = () => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setConfirmationText('');
    setResetConfirmDialogOpen(true);
  };

  // Fermer le dialogue de confirmation
  const handleCloseResetConfirmDialog = () => {
    setResetConfirmDialogOpen(false);
    setConfirmationText('');
    setResetInProgress(false);
  };

  // Réinitialiser toutes les personnalisations produits (vider customizationSections)
  const handleResetAllProductRules = async (): Promise<void> => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    if (confirmationText.toLowerCase() !== 'confirmer') {
      return;
    }

    try {
      setResetInProgress(true);
      const prods = products.filter(product => {
        const cat = categories.find(cat => cat.id === product.category);
        return !!cat && !cat.isAddonCategory;
      });
      const clearPromises = prods.map(prod =>
        productService.updateProduct(prod.id, { customizationSections: [], usesCategoryRules: true })
      );
      await Promise.all(clearPromises);
      await loadCategoriesAndCustomizableProducts();
      handleCloseResetConfirmDialog();
    } catch (error) {
      console.error('Erreur lors de la réinitialisation de tous les produits:', error);
      setError('Erreur lors de la réinitialisation de tous les produits. Veuillez réessayer.');
    } finally {
      setResetInProgress(false);
    }
  };

  // Ouvrir le dialogue pour éditer les règles de personnalisation d'un produit
  const handleEditProductRules = (product: Product) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setSelectedProductId(product.id);
    setSelectedProductName(product.name);
    setExistingProductRules({ category: product.id, sections: product.customizationSections || [] });
    setSelectedProductIngredients(getRemovableIngredientLabels(product));
    setProductCustomizationDialogOpen(true);
  };

  // Sauvegarder les règles de personnalisation pour un produit
  const handleSaveProductCustomizationRules = async (rules: CategoryCustomizationRules): Promise<void> => {
    if (!selectedProductId) return;
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      throw new Error(MENU_CATALOG_POSCAISSE_MESSAGE);
    }
    try {
      await productService.updateProduct(selectedProductId, { customizationSections: rules.sections });
      await loadCategoriesAndCustomizableProducts();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde des règles de personnalisation du produit:', err);
      setError('Erreur lors de la sauvegarde des règles du produit. Veuillez réessayer.');
      throw err;
    } finally {
      setProductCustomizationDialogOpen(false);
      setSelectedProductId(null);
      setSelectedProductName('');
      setExistingProductRules(null);
    }
  };

  // Rendu des différentes vues en fonction de l'onglet actif
  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      );
    }
    
    switch (activeTab) {
      case 'categories':
        return renderCategoriesTab();
      case 'products':
        return renderProductsTab();
      case 'simulator':
        return renderSimulatorTab();
      default:
        return null;
    }
  };
  
  // Rendu de l'onglet "Par catégorie"
  const renderCategoriesTab = () => {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="error"
            startIcon={<RestartAltIcon />}
            onClick={handleOpenResetConfirmDialog}
            disabled={isCatalogReadOnly}
            sx={{
              background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                boxShadow: '0 6px 20px rgba(220, 38, 38, 0.4)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            Réinitialiser tous les réglages
          </Button>
        </Box>
        {categoriesWithRules.length === 0 ? (
          <Alert severity="info">
            Aucune catégorie trouvée.
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Catégorie</TableCell>
                  <TableCell>Nombre de produits</TableCell>
                  <TableCell>Règles communes</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoriesWithRules.map((categoryWithRules) => (
                  <TableRow key={categoryWithRules.category.id}>
                    <TableCell>
                      <Typography variant="subtitle1">
                        {categoryWithRules.category.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{categoryWithRules.productsCount}</TableCell>
                    <TableCell>
                      {categoryWithRules.rules ? (
                        <Chip 
                          label={`${categoryWithRules.rules.sections.length} section(s)`}
                          color="success" 
                          size="small" 
                        />
                      ) : categoryWithRules.hasCommonRules ? (
                        <Chip 
                          label="Règles non extraites" 
                          color="warning" 
                          size="small" 
                        />
                      ) : (
                        <Chip 
                          label="Aucune règle" 
                          color="default" 
                          size="small" 
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleEditCategoryRules(categoryWithRules.category.id)}
                          title="Modifier les règles"
                          disabled={isCatalogReadOnly}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        
                        {categoryWithRules.hasCommonRules && !categoryWithRules.rules && (
                          <IconButton 
                            size="small" 
                            color="secondary"
                            onClick={() => handleOpenMigrationDialog(categoryWithRules.category.id)}
                            title="Extraire les règles communes"
                            disabled={isCatalogReadOnly}
                          >
                            <AutoFixHighIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </>
    );
  };
  
  // Pré-remplir et basculer vers l'onglet simulateur pour un produit donné
  const handleSimulateProduct = (product: Product) => {
    // Trouver l'ID de la catégorie correspondant à product.category (id ou nom)
    const productCat = product.category || '';
    const foundCategory = categories.find(
      c => matchesProductCategory(productCat, c.id) || c.name.toLowerCase() === productCat.toLowerCase()
    );
    if (foundCategory) {
      setSimulatorCategoryId(foundCategory.id);
    } else {
      setSimulatorCategoryId('');
    }
    setSimulatorProductId(product.id);
    handleResetSimulatorSelections();
    setActiveTab('simulator');
  };

  // Rendu de l'onglet "Par produit"
  const renderProductsTab = () => {
    const productsWithCustomizations = products.filter(product => {
      // Afficher tous les produits de catégories non-addon
      const category = categories.find(cat =>
        cat.id.toLowerCase() === product.category?.toLowerCase()
      );
      return !!category && !category.isAddonCategory;
    });
    // Appliquer filtres de catégorie et de recherche
    const filteredProducts = productsWithCustomizations.filter(product => {
      const matchesCategory = filterCategory
        ? product.category?.toLowerCase() === filterCategory.toLowerCase()
        : true;
      const matchesSearch = searchTerm ? product.name.toLowerCase().includes(searchTerm.toLowerCase()) : true;
      return matchesCategory && matchesSearch;
    });
    // Liste des produits override (custom spécifique)
    const overrideProducts = filteredProducts.filter(
      product => !productsInheritingRules[product.id]
    );

    return (
      <>
        <Box mb={3} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="category-filter-label">Filtrer par catégorie</InputLabel>
            <Select
              labelId="category-filter-label"
              value={filterCategory}
              label="Filtrer par catégorie"
              onChange={(e: any) => setFilterCategory(e.target.value)}
            >
              <MenuItem value="">Toutes les catégories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Rechercher"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
        </Box>
        {overrideProducts.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Overrides en attente :</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {overrideProducts.map(prod => (
                <Chip key={prod.id} label={prod.name} size="small" color="warning" />
              ))}
            </Box>
          </Box>
        )}
        {productsWithCustomizations.length === 0 ? (
          <Alert severity="info">
            Aucun produit avec des règles de personnalisation n'a été trouvé.
          </Alert>
        ) : (
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
              gap: 3 
            }}
          >
              {filteredProducts.map((product) => (
              <Card key={product.id} elevation={2}>
                <CardContent>
                  <Box display="flex" gap={2} mb={2}>
                    {product.imageUrl ? (
                      <Box
                        component="img"
                        src={product.imageUrl}
                        alt={product.name}
                        sx={{ width: 80, height: 80, objectFit: 'contain' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: 'grey.200',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: 1
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          No image
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="div">
                        {product.name}
                      </Typography>
                      {productsInheritingRules[product.id] ? (
                        <Chip label="Hérité" size="small" color="primary" sx={{ ml: 1 }} />
                      ) : (
                        <Chip label="Spécifique" size="small" color="warning" sx={{ ml: 1 }} />
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Catégorie: {getCategoryName(product.category)}
                      </Typography>
                      <Box display="flex" alignItems="center" mt={1}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={productsInheritingRules[product.id] || false}
                              onChange={(e) => handleToggleProductInheritance(product.id, e.target.checked)}
                              disabled={isCatalogReadOnly}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Utiliser les règles de catégorie
                            </Typography>
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box>
                    {renderCustomizationSections(product)}
                  </Box>
                  
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button 
                      variant="outlined" 
                      size="small" 
                      color="primary"
                      onClick={() => handleEditProductRules(product)}
                      startIcon={<EditIcon />}
                      disabled={isCatalogReadOnly}
                      sx={{ mr: 1 }}
                    >
                      Modifier
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="secondary"
                      onClick={() => handleSimulateProduct(product)}
                      startIcon={<VisibilityIcon />}
                    >
                      Simuler
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </>
    );
  };
  
  // Rendu de l'onglet "Simulateur"
  const renderSimulatorTab = () => {
    const nonAddonCategories = categories.filter(c => !c.isAddonCategory);
    const productMap = new Map(products.map(p => [p.id, p]));
    const selectedProduct = products.find(p => p.id === simulatorProductId) || null;

    const effectiveSections: any[] = selectedProduct
      ? getEffectiveCustomizationSections(selectedProduct)
      : [];

    // Validation et total
    const currentSelections = simSelections;
    const validationErrors: Record<string, string> = {};
    let total = selectedProduct ? (selectedProduct.discountPrice ?? selectedProduct.price) : 0;

    // Calcule les options pour une section (addons = produits de la catégorie d'addons)
    const getAddonOptions = (addonCategoryId?: string) => {
      if (!addonCategoryId) return [];
      const opts = getProductsByCategoryFlexible(addonCategoryId)
        .filter(p => p.isAvailable)
        .map(opt => ({
          id: opt.id,
          name: opt.name,
          price: opt.price,
          imageUrl: opt.imageUrl || undefined
        }));
      return opts;
    };

    // Calcule les suggestions (produits d'une catégorie donnée)
    const getSuggestedProducts = (productCategoryId?: string) => {
      if (!productCategoryId) return [];
      return getProductsByCategoryFlexible(productCategoryId).filter(p => p.isAvailable);
    };

    // Valider sélections + calculer total (suppléments payants)
    effectiveSections.forEach(section => {
      if (section.type === 'included_options' || section.type === 'paid_supplements') {
        const selectedIds = currentSelections[section.sectionId] || [];
        const minSel = section.minSelection ?? undefined;
        const maxSel = section.maxSelection ?? undefined;

        if (minSel !== undefined && selectedIds.length < minSel) {
          validationErrors[section.sectionId] = `Sélection minimale: ${minSel}`;
        }
        if (maxSel !== undefined && selectedIds.length > maxSel) {
          validationErrors[section.sectionId] = `Sélection maximale: ${maxSel}`;
        }

        if (section.type === 'paid_supplements') {
          selectedIds.forEach(id => {
            const optProd = productMap.get(id);
            if (optProd && optProd.isAvailable) total += optProd.price;
          });
        }
      }
    });


    // Produits filtrés par catégorie sélectionnée
    const availableProducts = simulatorCategoryId
      ? products.filter(p => matchesProductCategory(p.category, simulatorCategoryId))
      : [];

    const handleCategoryChange = (val: string) => {
      setSimulatorCategoryId(val);
      setSimulatorProductId('');
      handleResetSimulatorSelections();
    };

    const handleProductChange = (val: string) => {
      setSimulatorProductId(val);
      handleResetSimulatorSelections();
    };

    const isSectionInvalid = (sectionId: string) => !!validationErrors[sectionId];

    // Ouvrir le résumé de simulation
    const handleOpenSimResult = () => {
      if (!selectedProduct) return;

      const basePrice = selectedProduct ? (selectedProduct.discountPrice ?? selectedProduct.price) : 0;

      const includedOptions = effectiveSections
        .filter((s: any) => s.type === 'included_options')
        .flatMap((s: any) => (currentSelections[s.sectionId] || [])
          .filter((id: string) => (productMap.get(id)?.isAvailable))
          .map((id: string) => {
            const p = productMap.get(id);
            return { id, name: p?.name || id };
          })
        );

      const paidSupplements = effectiveSections
        .filter((s: any) => s.type === 'paid_supplements')
        .flatMap((s: any) => (currentSelections[s.sectionId] || [])
          .filter((id: string) => (productMap.get(id)?.isAvailable))
          .map((id: string) => {
            const p = productMap.get(id);
            return { id, name: p?.name || id, price: p?.price ?? 0 };
          })
        );

      const suggestedProducts = effectiveSections
        .filter((s: any) => s.type === 'suggested_products')
        .flatMap((s: any) => (currentSelections[s.sectionId] || [])
          .filter((id: string) => (productMap.get(id)?.isAvailable))
          .map((id: string) => {
            const p = productMap.get(id);
            return { id, name: p?.name || id };
          })
        );

      const finalIngredients = getRemovableIngredientLabels(selectedProduct).filter(i => !simRemovedIngredients.includes(i));
      setSimResult({
        productName: selectedProduct.name,
        basePrice,
        total,
        includedOptions,
        paidSupplements,
        removedIngredients: simRemovedIngredients,
        finalIngredients,
        suggestedProducts,
        productImageUrl: selectedProduct.imageUrl || null,
        description: selectedProduct.description || '',
        xlPrice: selectedProduct.priceXL ?? null,
        hasXL: !!selectedProduct.priceXL
      });
      setSimResultDialogOpen(true);
    };

    const handleOpenMobilePreview = () => {
      if (!selectedProduct) return;

      setMobilePreviewQuantity(1);
      setMobilePreviewSize('normal');

      const basePrice = selectedProduct ? (selectedProduct.discountPrice ?? selectedProduct.price) : 0;

      const includedOptions = effectiveSections
        .filter((s: any) => s.type === 'included_options')
        .flatMap((s: any) => (currentSelections[s.sectionId] || [])
          .filter((id: string) => (productMap.get(id)?.isAvailable))
          .map((id: string) => {
            const p = productMap.get(id);
            return { id, name: p?.name || id };
          })
        );

      const paidSupplements = effectiveSections
        .filter((s: any) => s.type === 'paid_supplements')
        .flatMap((s: any) => (currentSelections[s.sectionId] || [])
          .filter((id: string) => (productMap.get(id)?.isAvailable))
          .map((id: string) => {
            const p = productMap.get(id);
            return { id, name: p?.name || id, price: p?.price ?? 0 };
          })
        );

      const suggestedProducts = effectiveSections
        .filter((s: any) => s.type === 'suggested_products')
        .flatMap((s: any) => (currentSelections[s.sectionId] || [])
          .filter((id: string) => (productMap.get(id)?.isAvailable))
          .map((id: string) => {
            const p = productMap.get(id);
            return { id, name: p?.name || id };
          })
        );

      const finalIngredients = (selectedProduct?.ingredients || []).filter(i => !simRemovedIngredients.includes(i));

      setSimResult({
        productName: selectedProduct.name,
        basePrice,
        total,
        includedOptions,
        paidSupplements,
        removedIngredients: simRemovedIngredients,
        finalIngredients,
        suggestedProducts,
        productImageUrl: selectedProduct.imageUrl || null,
        description: selectedProduct.description || '',
        xlPrice: selectedProduct.priceXL ?? null,
        hasXL: !!selectedProduct.priceXL
      });
      setSimMobilePreviewOpen(true);
    };

    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Simulateur de personnalisation
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          Visualisez l'expérience client pour la personnalisation d'un produit.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <FormControl sx={{ minWidth: 260 }}>
            <InputLabel id="sim-category-label">Catégorie</InputLabel>
            <Select
              labelId="sim-category-label"
              value={simulatorCategoryId}
              label="Catégorie"
              onChange={(e: any) => handleCategoryChange(e.target.value)}
            >
              <MenuItem value="">Sélectionner une catégorie</MenuItem>
              {nonAddonCategories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 300 }} disabled={!simulatorCategoryId}>
            <InputLabel id="sim-product-label">Produit</InputLabel>
            <Select
              labelId="sim-product-label"
              value={simulatorProductId}
              label="Produit"
              onChange={(e: any) => handleProductChange(e.target.value)}
            >
              <MenuItem value="">Sélectionner un produit</MenuItem>
              {availableProducts.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            color="secondary"
            onClick={handleResetSimulatorSelections}
            disabled={!selectedProduct}
            startIcon={<RestartAltIcon />}
          >
            Réinitialiser
          </Button>
        </Box>

        {!selectedProduct ? (
          <Alert severity="info">
            Sélectionnez une catégorie et un produit pour lancer la simulation.
          </Alert>
        ) : (
          <>
            <Card elevation={1} sx={{ mb: 3 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {selectedProduct.imageUrl ? (
                  <Avatar
                    variant="rounded"
                    src={selectedProduct.imageUrl}
                    alt={selectedProduct.name}
                    sx={{ width: 64, height: 64 }}
                  />
                ) : (
                  <Avatar variant="rounded" sx={{ width: 64, height: 64 }}>
                    {selectedProduct.name[0]}
                  </Avatar>
                )}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6">{selectedProduct.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Catégorie: {getCategoryName(selectedProduct.category)}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="body2" color="text.secondary">Prix de base</Typography>
                  <Typography variant="h6">
                    {(selectedProduct.discountPrice ?? selectedProduct.price).toFixed(2)} MAD
                  </Typography>
                </Box>
                <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
                <Box textAlign="right">
                  <Typography variant="body2" color="text.secondary">Total simulé</Typography>
                  <Typography variant="h5" color="primary">
                    {total.toFixed(2)} MAD
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {effectiveSections.length === 0 ? (
              <Alert severity="info">
                Aucun réglage de personnalisation pour ce produit (ni règles héritées de la catégorie).
              </Alert>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
                {effectiveSections.map((section: any) => {
                  const isAddonType = section.type === 'included_options' || section.type === 'paid_supplements';
                  const isRemovableIng = section.type === 'removable_ingredients';
                  const isSuggested = section.type === 'suggested_products';

                  const options = isAddonType ? getAddonOptions(section.addonCategory) : [];
                  const selectedIds = currentSelections[section.sectionId] || [];
                  const minSel = section.minSelection ?? undefined;
                  const maxSel = section.maxSelection ?? undefined;
                  const overLimit = maxSel !== undefined && selectedIds.length >= maxSel;

                  return (
                    <Card key={section.sectionId} elevation={1}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {section.title || 'Section'}
                          </Typography>
                          <Chip
                            label={getSectionTypeLabel(section.type)}
                            size="small"
                            color={isAddonType ? 'primary' : (isRemovableIng ? 'warning' : 'default')}
                            variant="outlined"
                          />
                        </Box>

                        {isAddonType && (
                          <>
                            <Typography variant="caption" color="text.secondary">
                              {minSel !== undefined || maxSel !== undefined
                                ? `Sélection${minSel !== undefined ? ` min ${minSel}` : ''}${minSel !== undefined && maxSel !== undefined ? ' / ' : ''}${maxSel !== undefined ? `max ${maxSel}` : ''}`
                                : 'Sélection libre'}
                              {section.selectionUI === 'radio' ? ' • Choix unique' : ' • Choix multiple'}
                            </Typography>

                            {section.selectionUI === 'radio' ? (
                              <RadioGroup
                                value={selectedIds[0] || ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const val = e.target.value;
                                  setSimSelections(prev => ({
                                    ...prev,
                                    [section.sectionId]: val ? [val] : []
                                  }));
                                }}
                              >
                                {options.length > 0 ? options.map(opt => (
                                  <FormControlLabel
                                    key={opt.id}
                                    value={opt.id}
                                    control={<Radio size="small" />}
                                    label={
                                      <Box display="flex" alignItems="center" gap={1}>
                                        {opt.imageUrl && (
                                          <Avatar src={opt.imageUrl} variant="rounded" sx={{ width: 24, height: 24 }} />
                                        )}
                                        <Typography variant="body2">
                                          {opt.name}
                                          {section.type === 'paid_supplements' && (
                                            <Typography component="span" variant="body2" color="text.secondary"> — +{opt.price?.toFixed(2)} MAD</Typography>
                                          )}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                )) : (
                                  <Typography variant="body2" color="text.secondary">Aucune option disponible</Typography>
                                )}
                              </RadioGroup>
                            ) : (
                              <FormGroup>
                                {options.length > 0 ? options.map(opt => {
                                  const checked = selectedIds.includes(opt.id);
                                  const disableNew = overLimit && !checked;
                                  return (
                                    <FormControlLabel
                                      key={opt.id}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={checked}
                                          disabled={disableNew}
                                          onChange={() => {
                                            setSimSelections(prev => {
                                              const current = prev[section.sectionId] || [];
                                              const next = checked
                                                ? current.filter(id => id !== opt.id)
                                                : [...current, opt.id];
                                              return { ...prev, [section.sectionId]: next };
                                            });
                                          }}
                                        />
                                      }
                                      label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                          {opt.imageUrl && (
                                            <Avatar src={opt.imageUrl} variant="rounded" sx={{ width: 24, height: 24 }} />
                                          )}
                                          <Typography variant="body2">
                                            {opt.name}
                                            {section.type === 'paid_supplements' && (
                                              <Typography component="span" variant="body2" color="text.secondary"> — +{opt.price?.toFixed(2)} MAD</Typography>
                                            )}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                  );
                                }) : (
                                  <Typography variant="body2" color="text.secondary">Aucune option disponible</Typography>
                                )}
                              </FormGroup>
                            )}

                            {isSectionInvalid(section.sectionId) && (
                              <FormHelperText error>{validationErrors[section.sectionId]}</FormHelperText>
                            )}
                          </>
                        )}

                        {isRemovableIng && (
                          <>
                            {getRemovableIngredientLabels(selectedProduct).length === 0 ? (
                              <Alert severity="info">Ce produit n'a pas d'ingrédients définis.</Alert>
                            ) : (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  Cochez pour retirer des ingrédients. Non supprimables: {section.nonRemovable?.length || 0}
                                </Typography>
                                <FormGroup sx={{ mt: 1 }}>
                                  {getRemovableIngredientLabels(selectedProduct).map(ing => {
                                    const disabled = (section.nonRemovable || []).includes(ing);
                                    const checked = simRemovedIngredients.includes(ing);
                                    return (
                                      <FormControlLabel
                                        key={ing}
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={checked}
                                            disabled={disabled}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                              const isChecked = e.target.checked;
                                              setSimRemovedIngredients(prev =>
                                                isChecked ? [...prev, ing] : prev.filter(i => i !== ing)
                                              );
                                            }}
                                          />
                                        }
                                        label={
                                          <Typography variant="body2">
                                            {ing} {disabled && <Typography component="span" variant="caption" color="text.secondary"> (non supprimable)</Typography>}
                                          </Typography>
                                        }
                                      />
                                    );
                                  })}
                                </FormGroup>
                              </>
                            )}
                          </>
                        )}

                        {isSuggested && (
                          <>
                            <Typography variant="caption" color="text.secondary">
                              Produits suggérés (non inclus au total)
                            </Typography>
                            <FormGroup sx={{ mt: 1 }}>
                              {(() => {
                                const suggestions = getSuggestedProducts(section.productCategory);
                                if (suggestions.length === 0) {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      Aucune suggestion disponible
                                    </Typography>
                                  );
                                }
                                return suggestions.map(sp => {
                                  const selected = (currentSelections[section.sectionId] || []).includes(sp.id);
                                  return (
                                    <FormControlLabel
                                      key={sp.id}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={selected}
                                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setSimSelections(prev => {
                                              const current = prev[section.sectionId] || [];
                                              const next = e.target.checked
                                                ? [...current, sp.id]
                                                : current.filter(id => id !== sp.id);
                                              return { ...prev, [section.sectionId]: next };
                                            });
                                          }}
                                        />
                                      }
                                      label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                          {sp.imageUrl && (
                                            <Avatar src={sp.imageUrl} variant="rounded" sx={{ width: 24, height: 24 }} />
                                          )}
                                          <Typography variant="body2">{sp.name}</Typography>
                                        </Box>
                                      }
                                    />
                                  );
                                });
                              })()}
                            </FormGroup>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Box display="flex" justifyContent="flex-end" gap={2}>
              {Object.keys(validationErrors).length > 0 && (
                <Alert severity="warning" sx={{ mr: 'auto' }}>
                  Certaines sections ne respectent pas les contraintes de sélection.
                </Alert>
              )}
              <Button
                variant="outlined"
                startIcon={<PhoneIphoneIcon />}
                onClick={handleOpenMobilePreview}
              >
                Aperçu mobile
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrowIcon />}
                disabled={Object.keys(validationErrors).length > 0}
                onClick={handleOpenSimResult}
              >
                Valider la simulation
              </Button>
            </Box>
          </>
        )}
      </Paper>
    );
  };

  // Pré-calcul pour l'aperçu mobile (dialog), indépendant du rendu de l'onglet
  const previewProduct = products.find(p => p.id === simulatorProductId) || null;
  const previewProductMap = new Map(products.map(p => [p.id, p]));
  const previewEffectiveSections: any[] = previewProduct
    ? getEffectiveCustomizationSections(previewProduct)
    : [];
  const previewGetAddonOptions = (addonCategoryId?: string) => {
    if (!addonCategoryId) return [];
    return getProductsByCategoryFlexible(addonCategoryId)
      .filter(p => p.isAvailable)
      .map(opt => ({
        id: opt.id,
        name: opt.name,
        price: opt.price,
        imageUrl: opt.imageUrl || undefined,
      }));
  };
  const previewGetSuggestedProducts = (productCategoryId?: string) => {
    if (!productCategoryId) return [];
    return getProductsByCategoryFlexible(productCategoryId).filter(p => p.isAvailable);
  };

  return (
    <Layout title="Gestion des Personnalisations">
      {activeScopeType !== 'store' || !selectedStoreId ? (
        <ScopeRequirementAlert
          requiredScope="store"
          title="Contexte magasin requis"
          description="Cette page gère les règles de personnalisation d'un magasin précis. Sélectionne un scope Magasin depuis la barre haute."
        />
      ) : (
        <>
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">Personnalisations</Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          Consultez et gérez les règles de personnalisation des produits.
        </Typography>
        
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab label="Par catégorie" value="categories" />
          <Tab label="Par produit" value="products" />
          <Tab label="Simulateur" value="simulator" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isCatalogReadOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {MENU_CATALOG_POSCAISSE_MESSAGE}
        </Alert>
      )}

      {renderContent()}

      {/* Dialogue d'édition des règles de personnalisation */}
      {selectedCategoryId && (
        <CustomizationDialog
          open={customizationDialogOpen}
          onClose={handleCloseCustomizationDialog}
          onSave={handleSaveCustomizationRules}
          category={selectedCategoryId}
          categoryName={selectedCategoryName}
          existingRules={existingRules || undefined}
          addonCategories={categories.filter(cat => cat.isAddonCategory)} 
          productCategories={categories.filter(cat => !cat.isAddonCategory)}
          productIngredients={selectedProductIngredients}
        />
      )}

      {/* Dialogue d'édition des règles de personnalisation pour un produit */}
      {selectedProductId && (
        <CustomizationDialog
          open={productCustomizationDialogOpen}
          onClose={() => setProductCustomizationDialogOpen(false)}
          onSave={handleSaveProductCustomizationRules}
          category={selectedProductId}
          categoryName={selectedProductName}
          existingRules={existingProductRules || undefined}
          addonCategories={categories.filter(cat => cat.isAddonCategory)} 
          productCategories={categories.filter(cat => !cat.isAddonCategory)}
          productIngredients={selectedProductIngredients}
        />
      )}

      {/* Dialogue de confirmation de réinitialisation */}
      <Dialog
        open={resetConfirmDialogOpen}
        onClose={!resetInProgress ? handleCloseResetConfirmDialog : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="error" />
            <Typography variant="h6">
              Confirmer la réinitialisation
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              <strong>ATTENTION : Cette action est irréversible !</strong>
            </Typography>
            <Typography variant="body2">
              Vous êtes sur le point de réinitialiser toutes les personnalisations de tous les produits. 
              Cette action supprimera définitivement toutes les règles de personnalisation spécifiques aux produits.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            Cette opération va :
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemText primary="Supprimer toutes les sections de personnalisation spécifiques aux produits" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Configurer tous les produits pour utiliser les règles de catégorie par défaut" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Effacer toutes les personnalisations individuelles créées" />
            </ListItem>
          </List>
          
          <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
            Pour confirmer cette action, veuillez saisir le mot <strong>"confirmer"</strong> ci-dessous :
          </Typography>
          
          <TextField
            fullWidth
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder="Tapez 'confirmer' pour continuer"
            disabled={resetInProgress || isCatalogReadOnly}
            error={confirmationText !== '' && confirmationText.toLowerCase() !== 'confirmer'}
            helperText={
              confirmationText !== '' && confirmationText.toLowerCase() !== 'confirmer'
                ? "Veuillez saisir exactement 'confirmer'"
                : ""
            }
          />
          
          {resetInProgress && (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleCloseResetConfirmDialog}
            disabled={resetInProgress}
          >
            Annuler
          </Button>
          
          <Button 
            variant="contained" 
            color="error"
            onClick={handleResetAllProductRules}
            disabled={
              confirmationText.toLowerCase() !== 'confirmer' ||
              resetInProgress ||
              isCatalogReadOnly
            }
            startIcon={resetInProgress ? <CircularProgress size={20} /> : <RestartAltIcon />}
          >
            {resetInProgress ? 'Réinitialisation...' : 'Réinitialiser tous les réglages'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue de migration des règles */}
      <Dialog
        open={migrationDialogOpen}
        onClose={() => migrationProgress !== 'running' && setMigrationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Migration des règles de personnalisation
        </DialogTitle>
        
        <DialogContent>
          {migratingCategoryId && (
            <>
              <Typography variant="body1" paragraph>
                Vous êtes sur le point d'extraire les règles communes de la catégorie {" "}
                <strong>{getCategoryName(migratingCategoryId)}</strong> et de les stocker au niveau de la catégorie.
              </Typography>
              
              <Typography variant="body2" paragraph>
                Cette opération va:
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemText primary="Analyser tous les produits de la catégorie pour identifier les règles communes" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Créer un modèle de règles au niveau de la catégorie" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Configurer tous les produits pour utiliser ces règles par défaut" />
                </ListItem>
              </List>
              
              {migrationProgress === 'running' && (
                <Box display="flex" justifyContent="center" my={3}>
                  <CircularProgress />
                </Box>
              )}
              
              {migrationProgress === 'success' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Migration réussie! Les règles communes ont été extraites et appliquées à tous les produits de la catégorie.
                </Alert>
              )}
              
              {migrationProgress === 'error' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  La migration a échoué. Assurez-vous que les produits de cette catégorie partagent des règles communes.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setMigrationDialogOpen(false)}
            disabled={migrationProgress === 'running'}
          >
            Fermer
          </Button>
          
          {migrationProgress === 'idle' && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleStartMigration}
              disabled={isCatalogReadOnly}
              startIcon={<AutoFixHighIcon />}
            >
              Extraire les règles
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* Dialogue de résultat du simulateur */}
      {simResult && (
        <Dialog
          open={simResultDialogOpen}
          onClose={() => setSimResultDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Résumé de la simulation</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle1" gutterBottom>{simResult.productName}</Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Prix de base" secondary={`${simResult.basePrice.toFixed(2)} MAD`} />
              </ListItem>
              {simResult.includedOptions.length > 0 && (
                <ListItem>
                  <ListItemText primary="Options incluses" secondary={simResult.includedOptions.map(o => o.name).join(', ')} />
                </ListItem>
              )}
              {simResult.paidSupplements.length > 0 && (
                <ListItem>
                  <ListItemText
                    primary="Suppléments payants"
                    secondary={simResult.paidSupplements.map(o => `${o.name} (+${o.price.toFixed(2)} MAD)`).join(', ')}
                  />
                </ListItem>
              )}
              {simResult.removedIngredients.length > 0 && (
                <ListItem>
                  <ListItemText primary="Ingrédients retirés" secondary={simResult.removedIngredients.join(', ')} />
                </ListItem>
              )}
              {simResult.suggestedProducts.length > 0 && (
                <ListItem>
                  <ListItemText primary="Produits suggérés" secondary={simResult.suggestedProducts.map(o => o.name).join(', ')} />
                </ListItem>
              )}
            </List>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="flex-end">
              <Typography variant="h6" color="primary">Total: {simResult.total.toFixed(2)} MAD</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSimResultDialogOpen(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>
      )}
      {simResult && (
        <Dialog
          open={simMobilePreviewOpen}
          onClose={() => setSimMobilePreviewOpen(false)}
          maxWidth="xs"
          fullWidth
          scroll="paper"
          PaperProps={{ sx: { height: '80vh', maxHeight: '80vh', m: 0, display: 'flex' } }}
        >
          <DialogTitle>Aperçu mobile</DialogTitle>
          <DialogContent dividers sx={{ pt: 2, px: 0, pb: 0, flex: 1, overflowY: 'auto' }}>
            <Box sx={{ mx: 'auto', mt: 1.5, pt: 1, border: '1px solid', borderColor: 'grey.300', borderRadius: 3, width: 360, maxWidth: '100%', bgcolor: 'background.paper', boxShadow: 3 }}>
              {/* Image header */}
              <Box sx={{ height: 180, bgcolor: 'transparent', pt: 2, borderTopLeftRadius: '20px', borderTopRightRadius: '20px', overflow: 'hidden' }}>
                {simResult.productImageUrl ? (
                  <Box
                    component="img"
                    src={simResult.productImageUrl || undefined}
                    alt={simResult.productName}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'transparent', borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}
                  />
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                    <VisibilityIcon />
                  </Box>
                )}
              </Box>

              {/* Content */}
              <Box sx={{ p: 2 }}>
                <Typography variant="h6">{simResult.productName}</Typography>
                {simResult.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {simResult.description}
                  </Typography>
                )}

                {/* Price and size selector */}
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6">
                    {((mobilePreviewSize === 'XL' && simResult.xlPrice) ? simResult.xlPrice! : simResult.basePrice).toFixed(2)} MAD
                  </Typography>
                  {simResult.hasXL && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant={mobilePreviewSize === 'normal' ? 'contained' : 'outlined'}
                        onClick={() => setMobilePreviewSize('normal')}
                      >
                        Normal
                      </Button>
                      <Button
                        size="small"
                        variant={mobilePreviewSize === 'XL' ? 'contained' : 'outlined'}
                        onClick={() => setMobilePreviewSize('XL')}
                      >
                        XL
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* Ingredients */}
                {simResult.finalIngredients && simResult.finalIngredients.length > 0 && (
                  <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <RestaurantMenuIcon fontSize="small" color="primary" />
                      <Typography variant="subtitle2">Ingrédients</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {simResult.finalIngredients.map((ing) => (
                        <Chip key={ing} label={ing} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Sections de personnalisation (interactives) */}
                <Box sx={{ mt: 2 }}>
                  {previewEffectiveSections.length === 0 ? (
                    <Alert severity="info">Aucune section de personnalisation pour ce produit.</Alert>
                  ) : (
                    previewEffectiveSections.map((section: any) => {
                      const isAddonType = section.type === 'included_options' || section.type === 'paid_supplements';
                      const isRemovableIng = section.type === 'removable_ingredients';
                      const isSuggested = section.type === 'suggested_products';

                      const options = isAddonType ? previewGetAddonOptions(section.addonCategory) : [];
                      const selectedIds = (simSelections[section.sectionId] || []) as string[];
                      const minSel = section.minSelection ?? undefined;
                      const maxSel = section.maxSelection ?? undefined;
                      const overLimit = maxSel !== undefined && selectedIds.length >= maxSel;

                      return (
                        <Card key={section.sectionId} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1.5 }}>
                          <CardContent sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {section.title || 'Section'}
                              </Typography>
                              <Chip
                                label={getSectionTypeLabel(section.type)}
                                size="small"
                                color={isAddonType ? 'primary' : (isRemovableIng ? 'warning' : 'default')}
                                variant="outlined"
                              />
                            </Box>

                            {isAddonType && (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  {minSel !== undefined || maxSel !== undefined
                                    ? `Sélection${minSel !== undefined ? ` min ${minSel}` : ''}${minSel !== undefined && maxSel !== undefined ? ' / ' : ''}${maxSel !== undefined ? `max ${maxSel}` : ''}`
                                    : 'Sélection libre'}
                                  {section.selectionUI === 'radio' ? ' • Choix unique' : ' • Choix multiple'}
                                </Typography>

                                {section.selectionUI === 'radio' ? (
                                  <RadioGroup
                                    value={selectedIds[0] || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                      const val = e.target.value;
                                      setSimSelections(prev => ({
                                        ...prev,
                                        [section.sectionId]: val ? [val] : []
                                      }));
                                    }}
                                  >
                                    {options.length > 0 ? options.map((opt: any) => (
                                      <FormControlLabel
                                        key={opt.id}
                                        value={opt.id}
                                        control={<Radio size="small" />}
                                        label={
                                          <Box display="flex" alignItems="center" gap={1}>
                                            {opt.imageUrl && (
                                              <Avatar src={opt.imageUrl} variant="rounded" sx={{ width: 24, height: 24 }} />
                                            )}
                                            <Typography variant="body2">
                                              {opt.name}
                                              {section.type === 'paid_supplements' && (
                                                <Typography component="span" variant="body2" color="text.secondary"> — +{opt.price?.toFixed(2)} MAD</Typography>
                                              )}
                                            </Typography>
                                          </Box>
                                        }
                                      />
                                    )) : (
                                      <Typography variant="body2" color="text.secondary">Aucune option disponible</Typography>
                                    )}
                                  </RadioGroup>
                                ) : (
                                  <FormGroup>
                                    {options.length > 0 ? options.map((opt: any) => {
                                      const checked = selectedIds.includes(opt.id);
                                      const disableNew = overLimit && !checked;
                                      return (
                                        <FormControlLabel
                                          key={opt.id}
                                          control={
                                            <Checkbox
                                              size="small"
                                              checked={checked}
                                              disabled={disableNew}
                                              onChange={() => {
                                                setSimSelections(prev => {
                                                  const current = prev[section.sectionId] || [];
                                                  const next = checked
                                                    ? current.filter((id: string) => id !== opt.id)
                                                    : [...current, opt.id];
                                                  return { ...prev, [section.sectionId]: next };
                                                });
                                              }}
                                            />
                                          }
                                          label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                              {opt.imageUrl && (
                                                <Avatar src={opt.imageUrl} variant="rounded" sx={{ width: 24, height: 24 }} />
                                              )}
                                              <Typography variant="body2">
                                                {opt.name}
                                                {section.type === 'paid_supplements' && (
                                                  <Typography component="span" variant="body2" color="text.secondary"> — +{opt.price?.toFixed(2)} MAD</Typography>
                                                )}
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                      );
                                    }) : (
                                      <Typography variant="body2" color="text.secondary">Aucune option disponible</Typography>
                                    )}
                                  </FormGroup>
                                )}
                              </>
                            )}

                            {isRemovableIng && (
                              <>
                                {getRemovableIngredientLabels(previewProduct).length === 0 ? (
                                  <Alert severity="info">Ce produit n'a pas d'ingrédients définis.</Alert>
                                ) : (
                                  <>
                                    <Typography variant="caption" color="text.secondary">
                                      Cochez pour retirer des ingrédients. Non supprimables: {section.nonRemovable?.length || 0}
                                    </Typography>
                                    <FormGroup sx={{ mt: 1 }}>
                                      {getRemovableIngredientLabels(previewProduct).map((ing: any) => {
                                        const disabled = (section.nonRemovable || []).includes(ing);
                                        const checked = simRemovedIngredients.includes(ing);
                                        return (
                                          <FormControlLabel
                                            key={ing}
                                            control={
                                              <Checkbox
                                                size="small"
                                                checked={checked}
                                                disabled={disabled}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                  const isChecked = e.target.checked;
                                                  setSimRemovedIngredients(prev =>
                                                    isChecked ? [...prev, ing] : prev.filter(i => i !== ing)
                                                  );
                                                }}
                                              />
                                            }
                                            label={
                                              <Typography variant="body2">
                                                {ing} {disabled && <Typography component="span" variant="caption" color="text.secondary"> (non supprimable)</Typography>}
                                              </Typography>
                                            }
                                          />
                                        );
                                      })}
                                    </FormGroup>
                                  </>
                                )}
                              </>
                            )}

                            {isSuggested && (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  Produits suggérés (non inclus au total)
                                </Typography>
                                <FormGroup sx={{ mt: 1 }}>
                                  {(() => {
                                    const suggestions = previewGetSuggestedProducts(section.productCategory);
                                    if (suggestions.length === 0) {
                                      return (
                                        <Typography variant="body2" color="text.secondary">
                                          Aucune suggestion disponible
                                        </Typography>
                                      );
                                    }
                                    return suggestions.map((sp: any) => {
                                      const selected = (simSelections[section.sectionId] || []).includes(sp.id);
                                      return (
                                        <FormControlLabel
                                          key={sp.id}
                                          control={
                                            <Checkbox
                                              size="small"
                                              checked={selected}
                                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                setSimSelections(prev => {
                                                  const current = prev[section.sectionId] || [];
                                                  const next = e.target.checked
                                                    ? [...current, sp.id]
                                                    : current.filter((id: string) => id !== sp.id);
                                                  return { ...prev, [section.sectionId]: next };
                                                });
                                              }}
                                            />
                                          }
                                          label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                              {sp.imageUrl && (
                                                <Avatar src={sp.imageUrl} variant="rounded" sx={{ width: 24, height: 24 }} />
                                              )}
                                              <Typography variant="body2">{sp.name}</Typography>
                                            </Box>
                                          }
                                        />
                                      );
                                    });
                                  })()}
                                </FormGroup>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </Box>

                {/* Personalization */}
                {(simResult.includedOptions.length > 0 || simResult.paidSupplements.length > 0) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Personnalisation</Typography>
                    {simResult.includedOptions.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Options incluses</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                          {simResult.includedOptions.map((o) => (
                            <Chip key={o.id} label={o.name} size="small" />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {simResult.paidSupplements.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Suppléments payants</Typography>
                        <List dense>
                          {simResult.paidSupplements.map((o) => (
                            <ListItem key={o.id} disableGutters secondaryAction={<Typography variant="body2">+{o.price.toFixed(2)} MAD</Typography>}>
                              <ListItemText primary={o.name} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Suggestions */}
                {simResult.suggestedProducts.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Suggestions</Typography>
                    <List dense>
                      {simResult.suggestedProducts.map((o) => (
                        <ListItem key={o.id} disableGutters>
                          <ListItemText primary={o.name} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Bottom bar mimic */}
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'grey.100', borderRadius: 3, p: 0.5 }}>
                    <IconButton size="small" onClick={() => setMobilePreviewQuantity(q => Math.max(1, q - 1))}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="body2" sx={{ mx: 1.5 }}>{mobilePreviewQuantity}</Typography>
                    <IconButton size="small" onClick={() => setMobilePreviewQuantity(q => q + 1)}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {(() => {
                      const baseForSize = (mobilePreviewSize === 'XL' && (simResult.xlPrice ?? previewProduct?.priceXL))
                        ? (simResult.xlPrice ?? (previewProduct?.priceXL ?? 0))
                        : (previewProduct ? (previewProduct.discountPrice ?? previewProduct.price) : 0);
                      const supplements = previewEffectiveSections
                        .filter((s: any) => s.type === 'paid_supplements')
                        .flatMap((s: any) => (simSelections[s.sectionId] || []))
                        .reduce((sum: number, id: string) => {
                          const p = previewProductMap.get(id);
                          return sum + ((p && p.isAvailable) ? (p.price ?? 0) : 0);
                        }, 0);
                      const total = (baseForSize + supplements) * mobilePreviewQuantity;
                      return (
                        <Button fullWidth variant="contained" color="primary">
                          Ajouter • {total.toFixed(2)} MAD
                        </Button>
                      );
                    })()}
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSimMobilePreviewOpen(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>
      )}
        </>
      )}
    </Layout>
  );
};

export default Customizations;
